/**
 * Perf HUD: always-on FPS viewer with a frame-time graph, plus an expanded
 * view with a CPU section breakdown, GPU time and renderer counters.
 *
 * Usage per frame:
 *   perf.frameStart();
 *   ...work...; perf.mark('physics');
 *   ...work...; perf.mark('chunks');
 *   perf.frameEnd();
 *
 * `mark(name)` attributes the time since the previous mark (or frameStart) to
 * `name`. DOM writes are throttled so the HUD itself stays out of the profile.
 */

import type * as THREE from 'three';

export const enum PerfMode {
  Off = 0,
  Compact = 1,
  Full = 2,
}

const HISTORY = 240;
const GRAPH_W = 160;
const GRAPH_H = 36;
const TEXT_INTERVAL_MS = 250;
const GRAPH_INTERVAL_MS = 100;

export interface PerfSnapshot {
  fps: number;
  low1: number;
  frameMs: number;
  maxFrameMs: number;
  cpuMs: number;
  gpuMs: number;
  sections: Record<string, number>;
  sectionMax: Record<string, number>;
}

/** GPU frame time via EXT_disjoint_timer_query_webgl2 (Chromium desktop). */
class GpuTimer {
  private readonly ext: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  private readonly pending: WebGLQuery[] = [];
  private active: WebGLQuery | null = null;
  /** Smoothed GPU ms, or -1 when unsupported. */
  ms = -1;

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.ext = gl.getExtension('EXT_disjoint_timer_query_webgl2');
  }

  begin(): void {
    if (!this.ext || this.active || this.pending.length > 4) return;
    const q = this.gl.createQuery();
    if (!q) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    this.active = q;
  }

  end(): void {
    if (!this.ext || !this.active) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push(this.active);
    this.active = null;
  }

  poll(): void {
    if (!this.ext) return;
    const gl = this.gl;
    const disjoint = gl.getParameter(this.ext.GPU_DISJOINT_EXT) as boolean;
    while (this.pending.length > 0) {
      const q = this.pending[0];
      if (!gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) break;
      if (!disjoint) {
        const ms = (gl.getQueryParameter(q, gl.QUERY_RESULT) as number) / 1e6;
        this.ms = this.ms < 0 ? ms : this.ms * 0.85 + ms * 0.15;
      }
      gl.deleteQuery(q);
      this.pending.shift();
    }
  }
}

export class PerfHud {
  mode: PerfMode = PerfMode.Compact;

  private readonly root: HTMLDivElement;
  private readonly fpsEl: HTMLDivElement;
  private readonly detailEl: HTMLDivElement;
  private readonly errEl: HTMLDivElement;
  private readonly graph: HTMLCanvasElement;
  private readonly gctx: CanvasRenderingContext2D;
  private readonly gpu: GpuTimer;

  private readonly history = new Float32Array(HISTORY);
  private readonly sorted = new Float32Array(HISTORY);
  private head = 0;
  private filled = 0;

  private lastFrameStart = 0;
  private lastMark = 0;
  private lastText = 0;
  private lastGraph = 0;

  // accumulators for the current text window
  private winFrames = 0;
  private winMs = 0;
  private winMax = 0;
  private winCpu = 0;
  private readonly winSections = new Map<string, number>();
  private readonly winSectionMax = new Map<string, number>();

  private snap: PerfSnapshot = {
    fps: 0, low1: 0, frameMs: 0, maxFrameMs: 0, cpuMs: 0, gpuMs: -1, sections: {}, sectionMax: {},
  };

  /** Extra lines for the full view (renderer counters, chunk queues, ...). */
  detailLines: () => string[] = () => [];
  /** Error / diagnostic lines; shown in every mode except Off. */
  errorLines: () => string[] = () => [];

  constructor(renderer: THREE.WebGLRenderer, parent: HTMLElement) {
    this.gpu = new GpuTimer(renderer.getContext() as WebGL2RenderingContext);

    this.root = document.createElement('div');
    this.root.setAttribute('data-perf-hud', '');
    this.root.style.cssText = `
      position:fixed; right:8px; top:8px; z-index:30; pointer-events:none;
      font:11px/1.4 monospace; color:#fff; text-align:right;
      text-shadow:0 1px 2px #000;`;

    const box = document.createElement('div');
    box.style.cssText = `
      display:inline-block; padding:4px 6px 5px; background:rgba(0,0,0,0.45);
      border:1px solid rgba(255,255,255,0.12);`;
    this.fpsEl = document.createElement('div');
    this.fpsEl.style.cssText = `font:700 13px/1.3 monospace; white-space:pre;`;
    this.graph = document.createElement('canvas');
    this.graph.width = GRAPH_W;
    this.graph.height = GRAPH_H;
    this.graph.style.cssText = `display:block; width:${GRAPH_W}px; height:${GRAPH_H}px; margin-top:3px;`;
    this.gctx = this.graph.getContext('2d')!;
    this.detailEl = document.createElement('div');
    this.detailEl.style.cssText = `white-space:pre; margin-top:4px; color:#cfe8cf; display:none;`;
    box.append(this.fpsEl, this.graph, this.detailEl);

    this.errEl = document.createElement('div');
    this.errEl.style.cssText = `white-space:pre-wrap; color:#ff8a8a; margin-top:4px; max-width:60vw;`;

    this.root.append(box, this.errEl);
    parent.appendChild(this.root);
    this.lastFrameStart = performance.now();
    this.lastMark = this.lastFrameStart;
  }

  /** Off -> Compact -> Full -> Off. */
  cycle(): PerfMode {
    this.setMode(((this.mode + 1) % 3) as PerfMode);
    return this.mode;
  }

  setMode(mode: PerfMode): void {
    this.mode = mode;
    this.root.style.display = mode === PerfMode.Off ? 'none' : 'block';
    this.detailEl.style.display = mode === PerfMode.Full ? 'block' : 'none';
    this.lastText = 0; // refresh immediately
  }

  frameStart(): void {
    const now = performance.now();
    const dt = now - this.lastFrameStart;
    this.lastFrameStart = now;
    this.lastMark = now;

    this.history[this.head] = dt;
    this.head = (this.head + 1) % HISTORY;
    if (this.filled < HISTORY) this.filled++;

    this.winFrames++;
    this.winMs += dt;
    if (dt > this.winMax) this.winMax = dt;

    this.gpu.poll();
    this.gpu.begin();
  }

  mark(name: string): void {
    const now = performance.now();
    const d = now - this.lastMark;
    this.lastMark = now;
    this.winSections.set(name, (this.winSections.get(name) ?? 0) + d);
    if (d > (this.winSectionMax.get(name) ?? 0)) this.winSectionMax.set(name, d);
  }

  frameEnd(): void {
    this.gpu.end();
    const now = performance.now();
    this.winCpu += now - this.lastFrameStart;

    if (now - this.lastText >= TEXT_INTERVAL_MS) {
      this.lastText = now;
      this.rollWindow();
      if (this.mode !== PerfMode.Off) this.drawText();
    }
    if (this.mode !== PerfMode.Off && now - this.lastGraph >= GRAPH_INTERVAL_MS) {
      this.lastGraph = now;
      this.drawGraph();
    }
  }

  snapshot(): PerfSnapshot {
    return this.snap;
  }

  private rollWindow(): void {
    const n = Math.max(1, this.winFrames);
    const sections: Record<string, number> = {};
    const sectionMax: Record<string, number> = {};
    for (const [k, v] of this.winSections) sections[k] = v / n;
    for (const [k, v] of this.winSectionMax) sectionMax[k] = v;

    // 1% low over the history ring: the frame time 99% of frames beat.
    const m = this.filled;
    const s = this.sorted;
    for (let i = 0; i < m; i++) s[i] = this.history[i];
    const view = s.subarray(0, m);
    view.sort();
    const p99 = m > 0 ? view[Math.min(m - 1, Math.floor(m * 0.99))] : 0;

    this.snap = {
      fps: this.winMs > 0 ? (this.winFrames * 1000) / this.winMs : 0,
      low1: p99 > 0 ? 1000 / p99 : 0,
      frameMs: this.winMs / n,
      maxFrameMs: this.winMax,
      cpuMs: this.winCpu / n,
      gpuMs: this.gpu.ms,
      sections,
      sectionMax,
    };

    this.winFrames = 0;
    this.winMs = 0;
    this.winMax = 0;
    this.winCpu = 0;
    this.winSections.clear();
    this.winSectionMax.clear();
  }

  private drawText(): void {
    const s = this.snap;
    this.fpsEl.textContent =
      `${s.fps.toFixed(0)} fps   1% low ${s.low1.toFixed(0)}   ${s.frameMs.toFixed(1)} ms`;
    this.fpsEl.style.color = s.fps >= 55 ? '#8dff8d' : s.fps >= 30 ? '#ffe27a' : '#ff8a8a';

    if (this.mode === PerfMode.Full) {
      const lines: string[] = [];
      lines.push(`cpu ${s.cpuMs.toFixed(2)} ms   gpu ${s.gpuMs >= 0 ? s.gpuMs.toFixed(2) + ' ms' : 'n/a'}   worst ${s.maxFrameMs.toFixed(1)} ms`);
      const parts: string[] = [];
      for (const k in s.sections) parts.push(`${k} ${s.sections[k].toFixed(2)}`);
      // wrap the section list at ~4 entries per line
      for (let i = 0; i < parts.length; i += 4) lines.push(parts.slice(i, i + 4).join('  '));
      for (const l of this.detailLines()) lines.push(l);
      this.detailEl.textContent = lines.join('\n');
    }
    const errs = this.errorLines();
    this.errEl.textContent = errs.length ? errs.join('\n') : '';
  }

  private drawGraph(): void {
    const g = this.gctx;
    g.clearRect(0, 0, GRAPH_W, GRAPH_H);
    g.fillStyle = 'rgba(0,0,0,0.35)';
    g.fillRect(0, 0, GRAPH_W, GRAPH_H);
    // full height = 33.3 ms; guide lines at 60 fps and 30 fps
    const scale = GRAPH_H / 33.3;
    const n = Math.min(this.filled, GRAPH_W);
    for (let i = 0; i < n; i++) {
      const ms = this.history[(this.head - 1 - i + HISTORY * 2) % HISTORY];
      const h = Math.min(GRAPH_H, Math.max(1, ms * scale));
      g.fillStyle = ms <= 17.5 ? '#5fdc6a' : ms <= 34 ? '#e8c84a' : '#ef5a5a';
      g.fillRect(GRAPH_W - 1 - i, GRAPH_H - h, 1, h);
    }
    g.fillStyle = 'rgba(255,255,255,0.35)';
    g.fillRect(0, GRAPH_H - 16.7 * scale, GRAPH_W, 1);
  }
}
